/**
 * Registration details of a finished document (owner 10-21, item 4): "(1-6 digit number)/year or
 * year + 1, SRO code 3 digit". The deed is registered offline at the Sub-Registrar office; the
 * executive then types these, and the document is removed for him. One set of rules for the browser
 * form and the Worker (and a DB trigger checks the year again, migration 0008).
 */
import { z } from 'zod';
import { sroByCode } from './sro';

/** India time (+5:30): a document finished at 23:59 on 31 December belongs to that year. */
export const IST_OFFSET_MIN = 330;

/** The calendar year (India time) in which the document was finished. */
export function finishYear(completedAtIso: string): number {
  return new Date(Date.parse(completedAtIso) + IST_OFFSET_MIN * 60_000).getUTCFullYear();
}

/**
 * The years a registration may carry: from the year the document was started up to the year after it
 * was finished (owner 10-21: "year or year + 1"; 10-21 (2): the registration number is entered straight
 * from the draft, so "finished" is the moment it is entered). Oldest first, at most the last 6.
 * `endIso` defaults to the start (old callers: the finish time is both).
 */
export function allowedRegistrationYears(startIso: string, endIso: string = startIso): number[] {
  const last = Math.max(finishYear(startIso), finishYear(endIso)) + 1;
  const first = Math.max(finishYear(startIso), last - 5);
  const out: number[] = [];
  for (let y = first; y <= last; y++) out.push(y);
  return out;
}

export const RegistrationInput = z.strictObject({
  number: z.string().trim().regex(/^\d{1,6}$/, 'Document number: 1 to 6 digits')
    .transform(s => s.replace(/^0+(?=\d)/, ''))
    .refine(s => s !== '0', 'Document number: 1 to 6 digits, not 0'),
  year: z.int().min(2000).max(2199),
  // Owner 10-21 (2): the code of an office in the IGRS list (3 or 4 digits: 101 … 1324).
  sro: z.string().trim().regex(/^\d{3,4}$/, 'SRO code: 3 or 4 digits')
    .refine(s => sroByCode(s) !== undefined, 'SRO code: not in the list of Sub-Registrar offices'),
});
export type RegistrationInput = z.infer<typeof RegistrationInput>;

/** Issues as {path, message}; empty = fine. Used by the form (live) and the Worker (422). */
export function registrationIssues(input: unknown, startIso: string, endIso?: string): { path: string; message: string }[] {
  const parsed = RegistrationInput.safeParse(input);
  const out = parsed.success ? [] : parsed.error.issues.map(i => ({ path: i.path.map(String).join('.') || 'registration', message: i.message }));
  const year = (input as { year?: unknown } | null)?.year;
  if (typeof year === 'number' && !out.some(i => i.path === 'year')) {
    const years = allowedRegistrationYears(startIso, endIso);
    if (!years.includes(year)) out.push({ path: 'year', message: years.length === 2 ? `Year: ${years[0]} or ${years[1]}` : `Year: ${years[0]} to ${years.at(-1)}` });
  }
  return out;
}

/** "123/2026, SRO 456" */
export function formatRegistration(r: { number: string; year: number; sro: string }): string {
  return `${r.number}/${r.year}, SRO ${r.sro}`;
}
